import { useRef, useState, useEffect } from 'react'
import * as faceapi from 'face-api.js'
import Icon from './Icon'
import Spinner from './Spinner'

const TOOLS = {
  freehand: { name: 'Freehand', icon: 'gesture' },
  circle: { name: 'Circle', icon: 'radio_button_unchecked' },
  rectangle: { name: 'Rectangle', icon: 'crop_square' },
  text: { name: 'Text', icon: 'text_fields' },
  eraser: { name: 'Eraser', icon: 'ink_eraser' },
}

export default function PhotoAnnotationModal({ imageDataUrl, onSave, onClose }) {
  // Drawing state
  const [tool, setTool] = useState('freehand')
  const [color, setColor] = useState('#ff6b6b')
  const [strokeWidth, setStrokeWidth] = useState(3)
  const [fontSize, setFontSize] = useState(16)
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPoint, setStartPoint] = useState(null)
  const [textInput, setTextInput] = useState(null)
  const [history, setHistory] = useState([])

  // Masking state
  const [maskFaces, setMaskFaces] = useState(false)
  const [maskPlates, setMaskPlates] = useState(false)
  const [maskedPreview, setMaskedPreview] = useState(null)
  const [isMasking, setIsMasking] = useState(false)
  const [modelsLoaded, setModelsLoaded] = useState(false)

  const canvasEl = useRef(null)
  const contextRef = useRef(null)
  const imageRef = useRef(null)

  // Load face detection models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/models/'
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL)
        setModelsLoaded(true)
      } catch (err) {
        console.warn('Face detection models failed to load:', err)
      }
    }
    loadModels()
  }, [])

  // Setup canvas
  useEffect(() => {
    if (!canvasEl.current) return

    const img = new Image()
    img.onload = () => {
      const canvas = canvasEl.current
      const maxWidth = Math.min(img.width, window.innerWidth - 40)
      const maxHeight = Math.min(img.height, window.innerHeight - 400)

      let width = img.width
      let height = img.height

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height)
        width = Math.floor(width * ratio)
        height = Math.floor(height * ratio)
      }

      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      contextRef.current = ctx
      ctx.drawImage(img, 0, 0, width, height)
      imageRef.current = img
      saveHistory()
    }
    img.src = imageDataUrl
  }, [imageDataUrl])

  const saveHistory = () => {
    const canvas = canvasEl.current
    const imageData = contextRef.current.getImageData(0, 0, canvas.width, canvas.height)
    setHistory((prev) => [...prev.slice(0, 10), imageData])
  }

  const redrawFromHistory = () => {
    const canvas = canvasEl.current
    const ctx = contextRef.current
    if (history.length > 0) {
      ctx.putImageData(history[history.length - 1], 0, 0)
    } else if (imageRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(imageRef.current, 0, 0)
    }
  }

  const handleMouseDown = (e) => {
    const canvas = canvasEl.current
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY
    const ctx = contextRef.current

    if (tool === 'text') {
      setTextInput({ x, y, text: '' })
      return
    }

    setIsDrawing(true)
    setStartPoint({ x, y })

    if (tool === 'freehand') {
      ctx.beginPath()
      ctx.moveTo(x, y)
    }
  }

  const handleMouseMove = (e) => {
    if (!isDrawing || tool === 'text') return

    const canvas = canvasEl.current
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY
    const ctx = contextRef.current

    if (tool === 'freehand') {
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineTo(x, y)
      ctx.stroke()
    } else if (tool === 'eraser') {
      ctx.clearRect(x - strokeWidth / 2, y - strokeWidth / 2, strokeWidth, strokeWidth)
    } else {
      redrawFromHistory()
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.fillStyle = 'transparent'

      if (tool === 'rectangle') {
        const w = x - startPoint.x
        const h = y - startPoint.y
        ctx.strokeRect(startPoint.x, startPoint.y, w, h)
      } else if (tool === 'circle') {
        const radius = Math.sqrt(Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2))
        ctx.beginPath()
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI)
        ctx.stroke()
      }
    }
  }

  const handleMouseUp = () => {
    if (!isDrawing) return
    setIsDrawing(false)
    setStartPoint(null)
    if (tool !== 'freehand') {
      contextRef.current.closePath()
    }
    saveHistory()
  }

  const getTouchCoordinates = (touch) => {
    const canvas = canvasEl.current
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY
    }
  }

  const handleTouchStart = (e) => {
    e.preventDefault()
    const touch = e.touches[0]
    const { x, y } = getTouchCoordinates(touch)
    const ctx = contextRef.current

    if (tool === 'text') {
      setTextInput({ x, y, text: '' })
      return
    }

    setIsDrawing(true)
    setStartPoint({ x, y })

    if (tool === 'freehand') {
      ctx.beginPath()
      ctx.moveTo(x, y)
    }
  }

  const handleTouchMove = (e) => {
    e.preventDefault()
    if (!isDrawing || tool === 'text') return

    const touch = e.touches[0]
    const { x, y } = getTouchCoordinates(touch)
    const ctx = contextRef.current

    if (tool === 'freehand') {
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineTo(x, y)
      ctx.stroke()
    } else if (tool === 'eraser') {
      ctx.clearRect(x - strokeWidth / 2, y - strokeWidth / 2, strokeWidth, strokeWidth)
    } else {
      redrawFromHistory()
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.fillStyle = 'transparent'

      if (tool === 'rectangle') {
        const w = x - startPoint.x
        const h = y - startPoint.y
        ctx.strokeRect(startPoint.x, startPoint.y, w, h)
      } else if (tool === 'circle') {
        const radius = Math.sqrt(Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2))
        ctx.beginPath()
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI)
        ctx.stroke()
      }
    }
  }

  const handleTouchEnd = () => {
    if (!isDrawing) return
    setIsDrawing(false)
    setStartPoint(null)
    if (tool !== 'freehand') {
      contextRef.current.closePath()
    }
    saveHistory()
  }

  const handleTextSubmit = (text) => {
    if (!text || !textInput) return
    const ctx = contextRef.current
    ctx.fillStyle = color
    ctx.font = `${fontSize}px Arial`
    ctx.fillText(text, textInput.x, textInput.y)
    setTextInput(null)
    saveHistory()
  }

  const handleUndo = () => {
    if (history.length < 2) return
    const newHistory = history.slice(0, -1)
    setHistory(newHistory)
    const lastImageData = newHistory[newHistory.length - 1]
    if (lastImageData) {
      contextRef.current.putImageData(lastImageData, 0, 0)
    }
  }

  const handleClear = () => {
    const canvas = canvasEl.current
    contextRef.current.clearRect(0, 0, canvas.width, canvas.height)
    contextRef.current.drawImage(imageRef.current, 0, 0)
    setHistory([])
  }

  const maskFacesAndPlates = async () => {
    try {
      setIsMasking(true)
      const canvas = document.createElement('canvas')
      canvas.width = imageRef.current.width
      canvas.height = imageRef.current.height
      const ctx = canvas.getContext('2d')

      ctx.drawImage(imageRef.current, 0, 0)

      // Detect and mask faces
      const detections = await faceapi.detectAllFaces(imageRef.current, new faceapi.TinyFaceDetectorOptions())
      detections.forEach((detection) => {
        const { x, y, width, height } = detection.detection.box
        const padding = 10
        ctx.save()
        ctx.filter = 'blur(25px)'
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
        ctx.fillRect(x - padding, y - padding, width + padding * 2, height + padding * 2)
        ctx.restore()
      })

      // Simple plate detection: look for rectangular regions with high contrast
      // This is a basic heuristic - for production, use a dedicated model
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      // Find areas that look like license plates (white/yellow text on dark background)
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]
        const g = data[i + 1]
        const b = data[i + 2]
        const brightness = (r + g + b) / 3
        // Mask high-contrast regions (simplified plate detection)
        if ((brightness > 150 || brightness < 50) && Math.abs(r - g) < 30) {
          data[i] = 0
          data[i + 1] = 0
          data[i + 2] = 0
        }
      }
      ctx.putImageData(imageData, 0, 0)

      const maskedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
      setMaskedPreview(maskedDataUrl)
      setMaskFaces(true)
      setMaskPlates(true)
      setIsMasking(false)
    } catch (err) {
      console.error('Masking failed:', err)
      setIsMasking(false)
    }
  }

  const handleExport = () => {
    const canvas = canvasEl.current
    canvas.toBlob((blob) => {
      const displayUrl = maskedPreview && (maskFaces || maskPlates) ? maskedPreview : canvas.toDataURL('image/jpeg', 0.9)
      // Create a final canvas combining annotations with masks if needed
      const finalCanvas = document.createElement('canvas')
      finalCanvas.width = canvas.width
      finalCanvas.height = canvas.height
      const finalCtx = finalCanvas.getContext('2d')

      if (maskedPreview && (maskFaces || maskPlates)) {
        const maskImg = new Image()
        maskImg.onload = () => {
          finalCtx.drawImage(maskImg, 0, 0)
          finalCtx.drawImage(canvas, 0, 0)
          finalCanvas.toBlob((finalBlob) => {
            onSave(finalCanvas.toDataURL('image/jpeg', 0.9))
          })
        }
        maskImg.src = maskedPreview
      } else {
        onSave(canvas.toDataURL('image/jpeg', 0.9))
      }
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-surface rounded-xl overflow-hidden flex flex-col max-w-4xl w-full max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-headline-md text-text-primary">Annotate Photo</h2>
          <button
            onClick={onClose}
            className="p-2 min-h-0 rounded-full hover:bg-surface-high transition text-text-secondary"
            aria-label="Close"
          >
            <Icon name="close" className="text-xl" />
          </button>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-black">
          <canvas
            ref={canvasEl}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="max-w-full max-h-full cursor-crosshair touch-none"
            style={{ display: 'block' }}
          />
          {textInput && (
            <input
              autoFocus
              type="text"
              value={textInput.text}
              onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
              onBlur={() => handleTextSubmit(textInput.text)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit(textInput.text)
                if (e.key === 'Escape') setTextInput(null)
              }}
              style={{
                position: 'fixed',
                left: `${textInput.x}px`,
                top: `${textInput.y}px`,
                fontSize: `${fontSize}px`,
                color,
                fontWeight: 'bold',
                border: `2px solid ${color}`,
                padding: '2px 4px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                zIndex: 1000,
              }}
            />
          )}
        </div>

        {/* Tools */}
        <div className="border-t border-border px-6 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            {/* Drawing Tools */}
            <div>
              <p className="text-label-sm text-text-muted mb-2">Drawing</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(TOOLS).map(([key, { name, icon }]) => (
                  <button
                    key={key}
                    onClick={() => setTool(key)}
                    className={`px-3 py-2 rounded-lg text-label-sm transition ${
                      tool === key
                        ? 'bg-accent text-surface-dark font-semibold'
                        : 'bg-surface-high text-text-primary hover:bg-surface-bright'
                    }`}
                  >
                    <Icon name={icon} className="text-base inline mr-1" />
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Masking Tools */}
            <div>
              <p className="text-label-sm text-text-muted mb-2">Masking</p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maskFaces}
                    disabled={isMasking}
                    onChange={maskFacesAndPlates}
                    className="w-5 h-5"
                  />
                  <span className="text-label-sm text-text-primary">
                    {isMasking ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        Detecting...
                      </div>
                    ) : (
                      'Blur faces & plates'
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Color & Size */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-label-sm text-text-muted block mb-1.5">Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded border border-border cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 text-label-sm font-mono"
                  placeholder="#ff6b6b"
                />
              </div>
            </div>

            <div>
              <label className="text-label-sm text-text-muted block mb-1.5">
                {tool === 'text' ? `Size: ${fontSize}px` : `Stroke: ${strokeWidth}px`}
              </label>
              <input
                type="range"
                min={tool === 'text' ? 12 : 1}
                max={tool === 'text' ? 48 : 15}
                value={tool === 'text' ? fontSize : strokeWidth}
                onChange={(e) => {
                  if (tool === 'text') {
                    setFontSize(parseInt(e.target.value))
                  } else {
                    setStrokeWidth(parseInt(e.target.value))
                  }
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border px-6 py-4 flex gap-2">
          <button
            onClick={handleUndo}
            disabled={history.length < 2}
            className="flex items-center justify-center gap-2 flex-1 border border-border text-text-primary text-label-md rounded-lg py-3 hover:bg-surface-high transition disabled:opacity-40"
          >
            <Icon name="undo" className="text-lg" />
            Undo
          </button>
          <button
            onClick={handleClear}
            className="flex items-center justify-center gap-2 flex-1 border border-status-urgent/40 text-status-urgent text-label-md rounded-lg py-3 hover:bg-status-urgent/10 transition"
          >
            <Icon name="delete" className="text-lg" />
            Clear
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 flex-1 border border-border text-text-primary text-label-md rounded-lg py-3 hover:bg-surface-high transition"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center justify-center gap-2 flex-1 bg-accent text-surface-dark font-semibold text-label-md rounded-lg py-3 hover:bg-accent-light transition"
          >
            <Icon name="check" className="text-lg" />
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
